import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import Landing from './pages/Landing';
import WhyRidePulse from './pages/WhyRidePulse';
import AdminConsole from './pages/AdminConsole';
import StatusPage from './pages/status/StatusPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import EmailVerified from './pages/EmailVerified';
import Contact from './pages/Contact';
import ContactThanks from './pages/ContactThanks';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import DarkModeToggle from './components/DarkModeToggle';
import EventInvitePage from './pages/EventInvitePage';
import PasswordResetRedirect from './pages/PasswordResetRedirect';
import GettingStarted from './pages/features/GettingStarted';
import Garage from './pages/features/Garage';
import Events from './pages/features/Events';
import Hosting from './pages/features/Hosting';
import Social from './pages/features/Social';
import GroupPulse from './pages/features/GroupPulse';
import Pro from './pages/features/Pro';
import Privacy from './pages/features/Privacy';
import Help from './pages/Help';
import About from './pages/About';
import NotFound from './pages/NotFound';
import MarketingHeader from './components/MarketingHeader';
import './index.css';

// Internal brand preview is development-only: the lazy import is dead-code
// eliminated from production bundles, so the page ships no code or route.
const BrandPreview = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./pages/BrandPreview'))
  : null;

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

function RouteAwareChrome() {
  const { pathname } = useLocation();
  const isAdmin = pathname === '/admin' || pathname.startsWith('/admin/');
  if (isAdmin) {
    return <DarkModeToggle />;
  }
  // Utility/account surfaces use UtilityPageFrame (own brand home header +
  // support footer), so only the theme toggle is needed here — no bottom nav.
  const isUtility = ['/auth', '/email-verified', '/event'].some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
  if (isUtility) {
    return <DarkModeToggle />;
  }
  return <MarketingHeader />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div className="App">
            <RouteAwareChrome />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/event/:eventId" element={<EventInvitePage />} />
              <Route path="/why-ridepulse" element={<WhyRidePulse />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/getting-started" element={<GettingStarted />} />
              <Route path="/garage" element={<Garage />} />
              <Route path="/events" element={<Events />} />
              <Route path="/hosting" element={<Hosting />} />
              <Route path="/social" element={<Social />} />
              <Route path="/grouppulse" element={<GroupPulse />} />
              <Route path="/pro" element={<Pro />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/help" element={<Help />} />
              <Route path="/about" element={<About />} />
              {BrandPreview && (
                <Route path="/brand-preview" element={
                  <React.Suspense fallback={<p>Loading preview...</p>}>
                    <BrandPreview />
                  </React.Suspense>
                } />
              )}
              <Route path="/auth/login" element={<Login />} />
              <Route path="/auth/email-verified" element={<EmailVerified />} />
              <Route path="/email-verified" element={<EmailVerified />} />
              <Route path="/legal/terms" element={<TermsOfService />} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/contact/thanks" element={<ContactThanks />} />
              <Route path="/auth/reset-password" element={<PasswordResetRedirect />} />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminConsole />
                </ProtectedRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
